import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { inventoryApi, purchaseApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-INV-02.18 — Return to supplier (RMA).
 *
 * Three-state workflow: draft → shipped → completed. Left pane lists
 * RMAs; right pane is the detail with state-appropriate actions.
 */
export function SupplierReturnsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const listQuery = useQuery({
    queryKey: ['inv', 'rmas'],
    queryFn: () => inventoryApi.listRmas({ limit: 100 }),
  });

  const { filteredRows, kpis } = (() => {
    const rows = listQuery.data ?? [];
    let draft = 0, shipped = 0, completed = 0, totalValue = 0;
    for (const r of rows) {
      if (r.status === 'draft') draft++;
      else if (r.status === 'shipped') shipped++;
      else if (r.status === 'completed') completed++;
      totalValue += parseFloat(r.total_value) || 0;
    }
    const filtered = statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows;
    return {
      filteredRows: filtered,
      kpis: { total: rows.length, draft, shipped, completed, totalValue },
    };
  })();

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'shipped',   label: 'Shipped' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Supplier returns"
        subtitle="RMAs — send defective, wrong, or excess inbound goods back to suppliers."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New RMA
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total RMAs" value={kpis.total} />
        <KpiTile label="Draft" value={kpis.draft}
          accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="In transit" value={kpis.shipped}
          accent={kpis.shipped > 0 ? 'amber' : 'default'} />
        <KpiTile label="Total return value" value={`${fmtCurrency(kpis.totalValue)}`} />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_30rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
           !filteredRows.length ? (
            <EmptyState
              message={statusFilter ? `No ${statusFilter} RMAs` : 'No RMAs yet'}
              hint={statusFilter ? 'Try a different filter.' : 'Create an RMA to return goods to a supplier.'}
              action={!statusFilter && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  + New RMA
                </button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>RMA #</th>
                    <th>Supplier</th>
                    <th>Status</th>
                    <th className="text-right">Value</th>
                    <th>Return date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r: any) => (
                    <tr key={r.id}
                        onClick={() => setSelected(r.id)}
                        className={`cursor-pointer ${selected === r.id ? '!bg-canvas-subtle' : ''}`}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{r.rma_number}</td>
                      <td className="text-sm text-ink-700 font-medium">{r.supplier_name}</td>
                      <td><RmaStatusBadge status={r.status} /></td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        ${parseFloat(r.total_value).toFixed(2)}
                      </td>
                      <td className="text-xs text-ink-500">
                        {new Date(r.return_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select an RMA"
              hint="Click a row on the left to view items and lifecycle actions."
            />
          ) : (
            <RmaDetail
              rmaId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['inv', 'rmas'] })}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateRmaModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['inv', 'rmas'] });
            setShowCreate(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function RmaStatusBadge({ status }: { status: string }) {
  const cls = {
    draft:     'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200',
    shipped:   'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
    completed: 'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200',
    cancelled: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200';
  return <span className={`badge ${cls} text-2xs`}>{status}</span>;
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function RmaDetail({ rmaId, onChanged }: { rmaId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['inv', 'rma', rmaId],
    queryFn: () => inventoryApi.getRma(rmaId),
  });

  const shipMut = useMutation({
    mutationFn: () => inventoryApi.shipRma(rmaId),
    onSuccess: (data) => {
      toast.success(`Shipped ${fmtCurrency(data.shippedValue ?? 0)}. JE posted.`);
      qc.invalidateQueries({ queryKey: ['inv', 'rma', rmaId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const completeMut = useMutation({
    mutationFn: () => inventoryApi.completeRma(rmaId),
    onSuccess: () => {
      toast.success('RMA completed');
      qc.invalidateQueries({ queryKey: ['inv', 'rma', rmaId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (detailQuery.isLoading) return <LoadingState />;
  if (detailQuery.error) return <ErrorState error={detailQuery.error} />;
  const r = detailQuery.data;

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-slate-500">{r.rma_number}</div>
          <div className="font-semibold">{r.supplier_name}</div>
          <div className="text-xs text-slate-600">{r.warehouse_code} — {r.warehouse_name}</div>
          {r.reason && <div className="text-xs text-slate-600 mt-1">Reason: {r.reason}</div>}
        </div>
        <span className={`badge ${
          r.status === 'completed' ? 'bg-sage-50 text-sage-700 ring-sage-200' :
          r.status === 'shipped' ? 'bg-amber-100 text-amber-700' :
          r.status === 'cancelled' ? 'bg-red-50 text-red-700 ring-red-200' :
          'bg-canvas-subtle text-ink-700 ring-ink-200'
        }`}>{r.status}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-4">
        <div>Created {new Date(r.created_at).toLocaleString()} by {r.created_by_name}</div>
        {r.shipped_at && <div>Shipped {new Date(r.shipped_at).toLocaleString()} by {r.shipped_by_name}</div>}
        {r.completed_at && <div>Completed {new Date(r.completed_at).toLocaleString()} by {r.completed_by_name}</div>}
        {r.journal_entry_number && <div>JE: {r.journal_entry_number}</div>}
      </div>

      <table className="table mb-4">
        <thead>
          <tr>
            <th>Product</th>
            <th className="text-right">Qty</th>
            <th className="text-right">Unit cost</th>
            <th className="text-right">Line value</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {r.lines.map((l: any) => (
            <tr key={l.id}>
              <td>
                <div className="text-xs font-medium">{l.product_name}</div>
                <div className="font-mono text-2xs text-slate-500">{l.sku}</div>
              </td>
              <td className="text-right">{parseFloat(l.quantity).toFixed(0)}</td>
              <td className="text-right">{fmtCurrency(parseFloat(l.unit_cost))}</td>
              <td className="text-right font-semibold">{fmtCurrency((parseFloat(l.quantity) * parseFloat(l.unit_cost)))}</td>
              <td className="text-xs">{l.reason_code ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between items-center border-t border-slate-200 pt-3">
        <div>
          <div className="text-xs text-slate-500">Total value</div>
          <div className="text-lg font-bold">{fmtCurrency(parseFloat(r.total_value))}</div>
        </div>
        <div className="flex gap-2">
          {r.status === 'draft' && (
            <button className="btn-primary" disabled={shipMut.isPending} onClick={() => shipMut.mutate()}>
              {shipMut.isPending ? 'Shipping…' : 'Ship to supplier'}
            </button>
          )}
          {r.status === 'shipped' && (
            <button className="btn-primary" disabled={completeMut.isPending} onClick={() => completeMut.mutate()}>
              {completeMut.isPending ? 'Completing…' : 'Mark completed'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateRmaModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<Array<{
    productId: string; productName?: string; quantity: number; unitCost: number;
    reasonCode?: 'defective' | 'wrong_item' | 'over_shipment' | 'other';
  }>>([]);
  const [productSearch, setProductSearch] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });
  const prodQuery = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const createMut = useMutation({
    mutationFn: () => inventoryApi.createRma({
      supplierId, warehouseId,
      reason: reason || undefined,
      lines: lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        unitCost: l.unitCost,
        reasonCode: l.reasonCode,
      })),
    }),
    onSuccess: (data) => {
      toast.success(`RMA ${data.rmaNumber} created as draft`);
      onCreated(data.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = supplierId && warehouseId && lines.length > 0
    && lines.every((l) => l.quantity > 0 && l.unitCost > 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Inventory · RMA</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New supplier return</h2>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 gap-3 flex-shrink-0">
        <div>
          <label className="label">Supplier</label>
          <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Select…</option>
            {supQuery.data?.map((s: any) => (
              <option key={s.id} value={s.id}>{s.code} — {s.legal_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Warehouse <span className="font-normal text-ink-400 normal-case tracking-normal">(goods leave from)</span></label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select…</option>
            {whQuery.data?.map((w: any) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. defective lot, wrong item shipped, over-delivery" />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="mb-2 eyebrow text-ink-500">Lines ({lines.length})</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden mb-3">
          <div className="p-2 border-b border-ink-100">
            <input
              className="input text-sm"
              placeholder="Search product to add…"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
              <div className="mt-1 max-h-40 overflow-y-auto">
                {(prodQuery.data?.data ?? []).map((p: any) => (
                  <button
                    key={p.id}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-canvas-subtle rounded transition-colors"
                    onClick={() => {
                      setLines((prev) => [...prev, {
                        productId: p.id, productName: p.name,
                        quantity: 1, unitCost: parseFloat(p.cost_price) || 0,
                        reasonCode: 'defective',
                      }]);
                      setProductSearch('');
                    }}
                  >
                    <span className="font-mono text-ink-700">{p.sku}</span>
                    <span className="text-ink-500"> — {p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {lines.length === 0 ? (
            <div className="p-4 text-center text-xs text-ink-400">Search above to add products</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Unit cost</th>
                  <th>Reason</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td className="text-xs text-ink-900">{l.productName}</td>
                    <td className="text-right">
                      <input
                        type="number" min="0.01" step="1" className="input w-20 text-right tabular-nums"
                        value={l.quantity}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: v } : x));
                        }}
                      />
                    </td>
                    <td className="text-right">
                      <input
                        type="number" min="0.01" step="0.01" className="input w-24 text-right tabular-nums"
                        value={l.unitCost}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitCost: v } : x));
                        }}
                      />
                    </td>
                    <td>
                      <select
                        className="input text-xs"
                        value={l.reasonCode ?? ''}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, reasonCode: e.target.value as any } : x))}
                      >
                        <option value="defective">Defective</option>
                        <option value="wrong_item">Wrong item</option>
                        <option value="over_shipment">Over-shipment</option>
                        <option value="other">Other</option>
                      </select>
                    </td>
                    <td>
                      <button
                        className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                      >✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || createMut.isPending} onClick={() => createMut.mutate()}>
          {createMut.isPending ? 'Creating…' : 'Create RMA'}
        </button>
      </div>
    </Modal>
  );
}
